import React, { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiDollarSign,
  FiBox,
  FiBriefcase,
  FiPlus,
  FiRepeat,
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiCalendar,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "../assets/css/CompanyDetails.css";
import { getCompanyData } from "../Service/apiService";

let cachedStats = {
  total: 0,
  new: 0,
  renewed: 0,
  lost: 0,
  active: 0,
  upcoming: 0,
  unpaid: 0,
  trial: 0,
};

// Helper to check if stats have real data (not just zeros)
const hasValidStats = (stats) => {
  return Object.values(stats).some(value => value > 0);
};

export default function CompanyDetails() {
  const navigate = useNavigate();
  
  // Initialize with cached data immediately
  const [stats, setStats] = useState(() => ({ ...cachedStats }));
  
  // Only show skeleton if we have no valid cached data
  const [loading, setLoading] = useState(() => !hasValidStats(cachedStats));

  useEffect(() => {
    // Only fetch if we don't have valid cached data
    if (!hasValidStats(cachedStats)) {
      fetchStats();
    }
  }, []);

  const handleCompaniesData = (key) => {
    navigate(`/dashboard/companies/${key}`);
  };

  const isWithinDateRange = (dateStr, rangeInDays = 90) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const pastLimit = new Date();
  pastLimit.setDate(now.getDate() - rangeInDays); // last N days
  return date >= pastLimit && date <= now;
};

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getCompanyData("all");
      const raw = Array.isArray(response?.data) ? response.data : [];

      const isNotInternal = (c) =>
        (c.companyType || "").toLowerCase() !== "internal";
      const companies = raw.filter(isNotInternal);

      const toLower = (val) => (val || "").toLowerCase().trim();

      const isInFutureRange = (dateStr, days = 90) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const now = new Date();
        const futureLimit = new Date();
        futureLimit.setDate(now.getDate() + days);
        return date > now && date <= futureLimit;
      };

      const totalCompanies = companies;
      const newCompanies = companies.filter(
        (c) =>
          toLower(c.companyType) === "new" &&
          c.invoiceDate &&
          toLower(c.companyStatus) === "active"
      );
 
const renewedCompanies = companies.filter(
  (c) =>
    c.invoiceDate &&                     
    toLower(c.companyType) === "renewal" &&
    toLower(c.paymentStatus) === "paid" &&
    toLower(c.companyStatus) === "active"
   
);
      const lostCompanies = companies.filter(
        (c) => toLower(c.companyType) === "lost"
      );
      const totalActiveCompanies = companies.filter(
        (c) =>
          c.invoiceDate &&
          toLower(c.companyStatus) === "active" &&
          toLower(c.companyType) !== "lost"
      );
      const upcomingRenewalsCompany = companies.filter(
        (c) =>
          toLower(c.companyType) === "renewal" &&
          toLower(c.companyStatus) === "active" &&
          isInFutureRange(c.subEndDate, 90)
      );
      const unpaidCompanies = companies.filter(
        (c) =>
          toLower(c.paymentStatus) === "unpaid" &&
          toLower(c.companyStatus) === "active"
      );
      const trialCompanies = companies.filter(
        (c) =>
          toLower(c.companyType) === "trial" &&
          toLower(c.paymentStatus) === "trial" &&
          toLower(c.companyStatus) === "active"
      );

      const computed = {
        total: totalCompanies.length,
        new: newCompanies.length,
        renewed: renewedCompanies.length,
        lost: lostCompanies.length,
        active: totalActiveCompanies.length,
        upcoming: upcomingRenewalsCompany.length,
        unpaid: unpaidCompanies.length,
        trial: trialCompanies.length,
      };



      setStats(computed);
      cachedStats = { ...computed };
    } catch (err) {
      console.error("Error loading company stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "—";
    return Number(num).toLocaleString();
  };

 const metrics = [
  {
    id: "total-companies",
    key: "totalCompanies",
    label: "Total Companies",
    value: formatNumber(stats.total),
    icon: FiBriefcase,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "new-companies",
    key: "newCompanies",
    label: "New Companies",
    value: formatNumber(stats.new),
    icon: FiPlus,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "renewed-companies",
    key: "renewedCompanies",
    label: "Renewed Companies",
    value: formatNumber(stats.renewed),
    icon: FiRepeat,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "lost-companies",
    key: "lostCompanies",
    label: "Lost Companies",
    value: formatNumber(stats.lost),
    icon: FiX,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "active-companies",
    key: "totalActiveCompanies",
    label: "Active Companies",
    value: formatNumber(stats.active),
    icon: FiCheck,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "upcoming-companies",
    key: "upcomingRenewalsCompany",
    label: "Upcoming Renewals",
    value: formatNumber(stats.upcoming),
    icon: FiClock,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "unpaid-companies",
    key: "unpaidCompanies",
    label: "Unpaid Companies",
    value: formatNumber(stats.unpaid),
    icon: FiAlertTriangle,
    color: "rgb(5, 55, 99)",
  },
  {
    id: "trial-companies",
    key: "trialCompanies",
    label: "Trial Companies",
    value: formatNumber(stats.trial),
    icon: FiCalendar,
    color: "rgb(5, 55, 99)",
  },
];

  const NumberSkeleton = () => (
    <div className="number-skeleton">
      <div className="skeleton-loader"></div>
    </div>
  );

  return (
    <div className="company-details-container">
      <div className="metrics-grid">
        {metrics.map(({ id, key, label, value, trend, icon: Icon, color }) => (
          <div
            key={id}
            className="metric-card"
            onClick={() => handleCompaniesData(key)}
          >
            <div className="metric-icon" style={{ background: color }}>
              <Icon size={18} />
            </div>

            <p className="metric-label">{label}</p>
            {loading ? (
              <NumberSkeleton />
            ) : (
              <p className="metric-value">{value}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}